import { action, makeObservable, observable, runInAction } from "mobx";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { AuthStore } from "stores/AuthStore";
import { AgentSkillService } from "services/AgentSkillService";
import AgentSkillLibraryService from "services/AgentSkillLibraryService";
import { ServiceError } from "services/BaseService";
import {
    AgentSkill,
    AgentSkillHistoryItem,
    AgentSkillInlineTestRequest,
    AgentSkillLibraryItem,
    AgentSkillListItem,
    AgentSkillSyncOrganizationsResponse,
    AgentSkillSyncPreviewRequest,
    AgentSkillSyncPreviewResponse,
    AgentSkillSyncRequest,
    AgentSkillSyncResponse,
    AgentSkillTestDatasetImportResponse,
    AgentSkillTestCase,
    AgentSkillTestRunRequest,
    AgentSkillTestRunResponse,
    AgentSkillToolDefinition,
    SaveAgentSkillRequest,
} from "components/AgentSkills/Models/AgentSkill";
import type { StreamingTokenResult } from "services/AcxAgentService";

@AcxStore
export default class AgentSkillsStore extends BaseStore {
    public static Tasks = {
        Load: "Load Skills",
        LoadHistory: (skillId: string) => `Load Skill History ${skillId}`,
        Save: "Save Skill",
        Delete: "Delete Skill",
        LoadAvailableTools: "Load Available Skill Tools",
        ImportTestDataset: "Import Skill Test Dataset",
        SaveTestDataset: "Save Skill Test Dataset",
        TestSkill: "Test Skill",
        LoadLibrary: "Load Skill Library",
        AddToLibrary: "Add Skill To Library",
        RemoveFromLibrary: "Remove Skill From Library",
        AddFromLibrary: (skillId: string) => `Add Skill From Library ${skillId}`,
        UpdateFromLibrary: (skillId: string) =>
            `Update Skill From Library ${skillId}`,
        LoadSyncOrganizations: "Load Skill Sync Organizations",
        PreviewLibrarySync: "Preview Skill Library Sync",
        SyncLibraryToTenants: "Sync Library Skills To Tenants",
    };

    private readonly service = new AgentSkillService();
    private readonly libraryService = new AgentSkillLibraryService();
    private readonly messageStore: MessageStore;
    private readonly authStore: AuthStore;

    @observable public skills: AgentSkillListItem[] = [];
    @observable public librarySkills: AgentSkillLibraryItem[] = [];
    @observable public availableTools: AgentSkillToolDefinition[] = [];

    constructor(private rootStore: IRootStore) {
        super("AgentSkillsStore");
        makeObservable(this);
        this.messageStore = this.rootStore.getStore(MessageStore);
        this.authStore = this.rootStore.getStore(AuthStore);
    }

    public get isCurrentOrganizationAuthcx(): boolean {
        return this.authStore.orgStore?.isCurrentOrganizationAuthcx() ?? false;
    }

    public get isUltraUser(): boolean {
        return this.authStore.isUserUltra();
    }

    public get currentOrganizationId(): string | undefined {
        return this.authStore.orgStore?.selectedOrganization?.id;
    }

    private logError(error: unknown, fallback: string) {
        if (error instanceof ServiceError) {
            this.messageStore.logError(error.errorMessage ?? error.message);
            return;
        }

        if (error instanceof Error) {
            this.messageStore.logError(`${fallback}: ${error.message}`);
            return;
        }

        this.messageStore.logError(fallback);
    }

    @action
    public loadSkills() {
        return this.setupAsyncTask(AgentSkillsStore.Tasks.Load, async () => {
            const skills = await this.service.getSkills();
            runInAction(() => {
                this.skills = skills ?? [];
            });
        });
    }

    public getSkill(id: string): Promise<AgentSkill> {
        return this.service.getSkill(id);
    }

    public async loadSkillHistory(
        id: string,
    ): Promise<AgentSkillHistoryItem[] | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.LoadHistory(id),
            () => this.service.getSkillHistory(id),
        );
        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    @action
    public loadLibrary() {
        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.LoadLibrary,
            async () => {
                const librarySkills = await this.libraryService.getLibrary();
                runInAction(() => {
                    this.librarySkills = librarySkills ?? [];
                });
            },
        );
    }

    public get availableToolNames(): string[] {
        return this.availableTools.map((tool) => tool.name);
    }

    @action
    public loadAvailableTools() {
        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.LoadAvailableTools,
            async () => {
                const tools = await this.service.getAvailableTools();
                runInAction(() => {
                    this.availableTools = tools ?? [];
                });
            },
        );
    }

    public saveSkill(request: SaveAgentSkillRequest, id?: string) {
        return this.setupAsyncTask(AgentSkillsStore.Tasks.Save, async () => {
            try {
                const saved = id
                    ? await this.service.updateSkill(id, request)
                    : await this.service.createSkill(request);
                this.messageStore.logMessage("Skill saved", "success");
                return saved;
            } catch (error) {
                if (error instanceof Error) {
                    this.messageStore.logError(
                        "Failed to save skill: " + error.message,
                    );
                }
                return undefined;
            }
        });
    }

    public addToLibrary(skill: AgentSkillListItem) {
        if (!this.authStore.isUserUltra()) {
            this.messageStore.logError(
                "You do not have permission to add skills to the library",
            );
            return;
        }

        if (!this.isCurrentOrganizationAuthcx) {
            this.messageStore.logError(
                "Skills can only be added to the library from the Authcx organization",
            );
            return;
        }

        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.AddToLibrary,
            async () => {
                try {
                    await this.libraryService.addToLibrary(skill.id);
                    runInAction(() => {
                        this.skills = this.skills.map((currentSkill) =>
                            currentSkill.id === skill.id
                                ? { ...currentSkill, isInLibrary: true }
                                : currentSkill,
                        );
                    });
                    this.messageStore.logMessage(
                        "Skill added to library",
                        "success",
                    );
                    await this.loadLibrary();
                } catch (error) {
                    this.logError(error, "Failed to add skill to library");
                }
            },
        );
    }

    public removeFromLibrary(skill: AgentSkillListItem) {
        if (!this.authStore.isUserUltra()) {
            this.messageStore.logError(
                "You do not have permission to remove skills from the library",
            );
            return;
        }

        if (!this.isCurrentOrganizationAuthcx) {
            this.messageStore.logError(
                "Skills can only be removed from the library from the Authcx organization",
            );
            return;
        }

        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.RemoveFromLibrary,
            async () => {
                try {
                    await this.libraryService.removeFromLibrary(skill.id);
                    runInAction(() => {
                        this.skills = this.skills.map((currentSkill) =>
                            currentSkill.id === skill.id
                                ? { ...currentSkill, isInLibrary: false }
                                : currentSkill,
                        );
                    });
                    this.messageStore.logMessage(
                        "Skill removed from library",
                        "success",
                    );
                    await this.loadLibrary();
                } catch (error) {
                    this.logError(error, "Failed to remove skill from library");
                }
            },
        );
    }

    public addFromLibrary(librarySkill: AgentSkillLibraryItem) {
        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.AddFromLibrary(librarySkill.id),
            async () => {
                try {
                    await this.libraryService.addFromLibrary(librarySkill.id);
                    runInAction(() => {
                        librarySkill.existsInTenant = true;
                    });
                    this.messageStore.logMessage(
                        "Skill added from library",
                        "success",
                    );
                    await this.loadSkills();
                    await this.loadLibrary();
                } catch (error) {
                    this.logError(error, "Failed to add skill from library");
                }
            },
        );
    }

    public updateFromLibrary(librarySkill: AgentSkillLibraryItem) {
        const tenantSkillId = librarySkill.tenantSkillId;
        if (!tenantSkillId) {
            this.messageStore.logError(
                "A tenant skill eligible for update could not be found",
            );
            return;
        }

        return this.setupAsyncTask(
            AgentSkillsStore.Tasks.UpdateFromLibrary(librarySkill.id),
            async () => {
                try {
                    await this.libraryService.updateFromLibrary(
                        tenantSkillId,
                        librarySkill.id,
                    );
                    this.messageStore.logMessage(
                        "Skill updated from library",
                        "success",
                    );
                    await this.loadSkills();
                    await this.loadLibrary();
                } catch (error) {
                    this.logError(error, "Failed to update skill from library");
                }
            },
        );
    }

    public async loadSyncOrganizations(): Promise<
        AgentSkillSyncOrganizationsResponse | undefined
    > {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.LoadSyncOrganizations,
            () => this.libraryService.getSyncOrganizations(),
        );
        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public async previewLibrarySync(
        request: AgentSkillSyncPreviewRequest,
    ): Promise<AgentSkillSyncPreviewResponse | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.PreviewLibrarySync,
            () => this.libraryService.previewSync(request),
        );
        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public async syncLibraryToTenants(
        request: AgentSkillSyncRequest,
    ): Promise<AgentSkillSyncResponse | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.SyncLibraryToTenants,
            async () => {
                const response = await this.libraryService.syncToTenants(request);
                this.messageStore.logMessage("Library skills synchronized", "success");
                return response;
            },
        );
        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public async importTestDataset(
        file: File,
    ): Promise<AgentSkillTestDatasetImportResponse | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.ImportTestDataset,
            async () => {
                try {
                    return await this.service.importTestDataset(file);
                } catch (error) {
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to import test dataset: " + error.message,
                        );
                    }
                    return undefined;
                }
            },
        );

        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public async saveTestDataset(
        id: string,
        testCases: AgentSkillTestCase[],
    ): Promise<AgentSkill | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.SaveTestDataset,
            async () => {
                try {
                    const saved = await this.service.saveTestDataset(id, {
                        testCases,
                    });
                    this.messageStore.logMessage(
                        "Skill test dataset saved",
                        "success",
                    );
                    return saved;
                } catch (error) {
                    this.logError(error, "Failed to save skill test dataset");
                    return undefined;
                }
            },
        );

        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public async testSkill(
        request: AgentSkillTestRunRequest,
    ): Promise<AgentSkillTestRunResponse | undefined> {
        const result = await this.setupAsyncTask(
            AgentSkillsStore.Tasks.TestSkill,
            async () => {
                try {
                    return await this.service.testSkill(request);
                } catch (error) {
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to test skill: " + error.message,
                        );
                    }
                    return undefined;
                }
            },
        );

        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    public testSkillInline(
        request: AgentSkillInlineTestRequest,
        abortSignal?: AbortSignal,
    ): AsyncIterable<StreamingTokenResult> {
        return this.service.testSkillInline(request, abortSignal);
    }

    public deleteSkill(id: string) {
        return this.setupAsyncTask(AgentSkillsStore.Tasks.Delete, async () => {
            try {
                await this.service.deleteSkill(id);
                this.messageStore.logMessage("Skill deleted", "success");
                runInAction(() => {
                    this.skills = this.skills.filter((s) => s.id !== id);
                });
                return true;
            } catch (error) {
                if (error instanceof Error) {
                    this.messageStore.logError(
                        "Failed to delete skill: " + error.message,
                    );
                }
                return false;
            }
        });
    }
}
